import {
  type AuthenticatedUserWithSelectedRole,
  type ClientAuthenticatedUserWithSelectedRole,
} from 'connect-graphql/auth';
import { AppRole, OrganizationType } from 'connect-graphql/generated';

import { titleCase } from '@craft/lib/formatters';
import { extractKeys } from '@craft/lib/primitives';

/**
 * Converts an incoming Prisma enum value into the corresponding connect-graphql enum member.
 *
 * The Prisma values are upper-snake-case while the connect-graphql enum keys are title-case (e.g.
 * the Prisma `'EDUCATION_PROVIDER'` maps to the {@link OrganizationType.EducationProvider} key), so
 * the value is title-cased to derive the target key, which is then resolved against the enum and
 * returned under that enum's type with no cast.
 *
 * @param {E} graphQlEnum
 *   The connect-graphql generated enum to convert the value into.
 * @param {`${E[keyof E]}`} value
 *   The incoming upper-snake-case Prisma enum value, whose title-cased form must be a key of
 *   {@link graphQlEnum}.
 *
 * @throws {Error} When the title-cased {@link value} is not a key of {@link graphQlEnum}.
 *
 * @returns {E[keyof E]}
 *   The matching {@link graphQlEnum} member.
 */
const toGraphQlEnum = <E extends Record<string, string>>(
  graphQlEnum: E,
  value: `${E[keyof E]}`,
): E[keyof E] => {
  const expectedKey = titleCase(value, true);
  const key = extractKeys(graphQlEnum).find(candidate => candidate === expectedKey);
  if (key === undefined) {
    throw new Error(`The value "${value}" is not a member of the provided GraphQL enum.`);
  }
  return graphQlEnum[key];
};

export const toClientAuthenticatedUser = (
  user: AuthenticatedUserWithSelectedRole,
): ClientAuthenticatedUserWithSelectedRole => ({
  ...user,
  organizationRoles: user.organizationRoles.map(orgRole => ({
    ...orgRole,
    organization: {
      ...orgRole.organization,
      type: toGraphQlEnum(OrganizationType, orgRole.organization.type),
    },
    role: toGraphQlEnum(AppRole, orgRole.role),
  })),
  selectedOrganizationRole: {
    ...user.selectedOrganizationRole,
    organization: {
      ...user.selectedOrganizationRole.organization,
      type: toGraphQlEnum(OrganizationType, user.selectedOrganizationRole.organization.type),
    },
    role: toGraphQlEnum(AppRole, user.selectedOrganizationRole.role),
  },
});
